import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { HostedAgentToolCatalogEntry } from "../hooks/admin-queries"
import type {
  HostedAgentFormState,
  HostedAgentToolGroup,
  HostedAgentToolOverrideField,
} from "../utils/hosted-agents"

import { Search, SlidersHorizontal, X } from "@/components/icons"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import { HostedAgentActionPicker } from "./hosted-agent-action-picker"
import { setHostedAgentToolAction } from "../utils/hosted-agent-permissions"
import {
  getHostedAgentToolOverrideValue,
  groupHostedAgentTools,
  HOSTED_AGENT_TOOL_BINDINGS,
  HOSTED_AGENT_TOOL_GROUP_LABELS,
  isHostedAgentToolOverrideOpaque,
  setHostedAgentToolOverrideValue,
} from "../utils/hosted-agents"

// Tools picker tab of the hosted-agent edit wizard: search over the tool
// registry catalog, checkbox rows grouped into sections, the current selection
// as chips, a per-tool limits editor over `tool_overrides` (max_tokens /
// max_calls_per_iteration — the fields the runtime consumes), and (A3.1) a
// per-action allowlist picker over `tool_actions` for tools that expose an
// ACTIONS concept.
//
// #3022: rows are sectioned by the catalog's `group` (derived server-side from
// the manifest tags; unknown/absent → "other"), search filters before the
// grouping so a query only leaves the sections that still match. Two markings
// are carried on the row itself: approval-gated tools/actions, which are
// fail-closed for hosted agents (no approval channel — the gateway drops them
// and answers 403 agent_tool_approval_required), and tools that need a binding
// from another tab to return anything (library_search → Libraries).
//
// tool_actions semantics (#3030 punkt 1): an ABSENT allowlist means "all
// actions allowed". An explicit EMPTY list is never saved by this UI (the
// final uncheck removes the tool atomically, see setHostedAgentToolAction,
// and buildHostedAgentSavePayload drops empty allowlists) and is rejected by
// the backend on write; a legacy stored [] is interpreted fail-closed at
// runtime (nothing allowed — the tool is dropped from declarations and every
// call 403s). Selecting a proper subset restricts the agent to only those
// actions; the gateway refuses anything else with 403
// agent_tool_action_denied. Explicit action grants stay explicit. Other
// overrides are preserved and editable in the Raw JSON tab.

export function HostedAgentToolsTab({
  form,
  patch,
  catalog,
  isLoadingCatalog,
  catalogFailed,
}: {
  form: HostedAgentFormState
  patch: (partial: Partial<HostedAgentFormState>) => void
  catalog: HostedAgentToolCatalogEntry[]
  isLoadingCatalog: boolean
  catalogFailed: boolean
}) {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const [configuringTool, setConfiguringTool] = useState<string | null>(null)

  const overrideToolNames = Object.keys(form.toolOverrides)
  const knownNames = useMemo(() => new Set(catalog.map((tool) => tool.name)), [catalog])
  // Keep already-selected tools visible even when the catalog misses them
  // (e.g. a tool registered on the server but not exposed by any runtime
  // agent) so a save never silently drops them.
  const unknownSelected = form.tools.filter((tool) => !knownNames.has(tool))
  const rows: HostedAgentToolCatalogEntry[] = useMemo(
    () => [
      ...unknownSelected.map((name) => ({
        name,
        description: "",
        tags: [] as string[],
        actions: [] as string[],
        group: "",
        requiresApproval: false,
        approvalGatedActions: [] as string[],
      })),
      ...catalog,
    ],
    [catalog, unknownSelected]
  )

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return rows
    return rows.filter(
      (tool) =>
        tool.name.toLowerCase().includes(query) ||
        tool.description.toLowerCase().includes(query) ||
        tool.tags.some((tag) => tag.toLowerCase().includes(query))
    )
  }, [rows, search])

  // Sections come from the backend `group`; search runs first so an active
  // query collapses the list to the sections that still have a hit.
  //
  // Unknown selected tools stay OUT of the grouping: they carry no `group`, so
  // grouping them would sort them into "Other" — last, behind a scroll, sorted
  // alphabetically — which is the opposite of the "keep what is already saved
  // in plain sight" reason they are prepended in the first place.
  const unknownNames = useMemo(() => new Set(unknownSelected), [unknownSelected])
  const unknownRows = useMemo(
    () => filteredRows.filter((tool) => unknownNames.has(tool.name)),
    [filteredRows, unknownNames]
  )
  const groupedRows = useMemo(
    () => groupHostedAgentTools(filteredRows.filter((tool) => !unknownNames.has(tool.name))),
    [filteredRows, unknownNames]
  )

  const toggle = (tool: string, checked: boolean) => {
    patch({
      tools: checked ? [...form.tools, tool] : form.tools.filter((entry) => entry !== tool),
    })
    if (!checked && configuringTool === tool) setConfiguringTool(null)
  }

  const setOverride = (tool: string, field: HostedAgentToolOverrideField, raw: string) => {
    patch({
      toolOverrides: setHostedAgentToolOverrideValue({
        overrides: form.toolOverrides,
        tool,
        field,
        raw,
      }),
    })
  }

  // A3.1: toggle one action in a tool's allowlist. Starting from "all allowed"
  // (empty/absent allowlist), the first uncheck materializes the full set minus
  // that action. Removing the final action disables the entire tool.
  const toggleAction = (params: {
    tool: string
    action: string
    allActions: string[]
    checked: boolean
  }) => {
    const next = setHostedAgentToolAction({
      ...params,
      tools: form.tools,
      toolActions: form.toolActions,
    })
    patch(next)
    if (form.tools.includes(params.tool) && !next.tools.includes(params.tool)) {
      setConfiguringTool(null)
      toast.info(
        t("adminDashboard.hostedAgents.toolsTab.lastActionDisabled", {
          defaultValue: "No actions selected — the tool is now disabled. Save to apply.",
        })
      )
    }
  }

  const droppedOverrides = overrideToolNames.filter((tool) => !form.tools.includes(tool))

  const renderOverrideEditor = (tool: string) => {
    if (isHostedAgentToolOverrideOpaque(form.toolOverrides, tool)) {
      return (
        <p className="text-muted-foreground mt-2 text-xs">
          {t("adminDashboard.hostedAgents.toolsTab.overridesOpaque", {
            defaultValue:
              "This tool's overrides have a non-standard shape — edit them in the Raw JSON tab.",
          })}
        </p>
      )
    }
    return (
      <div className="bg-muted/30 mt-2 space-y-3 rounded-md border p-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor={`hosted-agent-tool-max-tokens-${tool}`}>
              {t("adminDashboard.hostedAgents.toolsTab.maxTokensLabel", {
                defaultValue: "Max tokens per result",
              })}
            </Label>
            <Input
              className="h-8"
              id={`hosted-agent-tool-max-tokens-${tool}`}
              inputMode="numeric"
              min={1}
              type="number"
              value={getHostedAgentToolOverrideValue(form.toolOverrides, tool, "max_tokens")}
              onChange={(event) => setOverride(tool, "max_tokens", event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor={`hosted-agent-tool-max-calls-${tool}`}>
              {t("adminDashboard.hostedAgents.toolsTab.maxCallsLabel", {
                defaultValue: "Max calls per iteration",
              })}
            </Label>
            <Input
              className="h-8"
              id={`hosted-agent-tool-max-calls-${tool}`}
              inputMode="numeric"
              min={1}
              type="number"
              value={getHostedAgentToolOverrideValue(
                form.toolOverrides,
                tool,
                "max_calls_per_iteration"
              )}
              onChange={(event) => setOverride(tool, "max_calls_per_iteration", event.target.value)}
            />
          </div>
        </div>
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.hostedAgents.toolsTab.overridesHint", {
            defaultValue:
              "Leave a field empty to use the platform default. Any other override fields are preserved and editable in the Raw JSON tab.",
          })}
        </p>
      </div>
    )
  }

  const groupLabel = (group: HostedAgentToolGroup) =>
    t(`adminDashboard.hostedAgents.toolsTab.groups.${group}`, {
      defaultValue: HOSTED_AGENT_TOOL_GROUP_LABELS[group],
    })

  // Sticky micro-label per DESIGN.md ("Section labels (uppercase, tracking)"
  // = 11px/600). `bg-background` matches the dialog surface so rows scroll
  // under the heading cleanly. `aria-labelledby` makes each section a named
  // region instead of an anonymous group of checkboxes.
  const renderToolSection = (section: {
    id: string
    label: string
    tools: HostedAgentToolCatalogEntry[]
  }) => {
    const headingId = `hosted-agent-tool-group-${section.id}`
    return (
      <section key={section.id} aria-labelledby={headingId} className="space-y-1.5">
        <h3
          className="bg-background text-muted-foreground sticky top-0 z-10 flex items-baseline gap-2 py-1 text-[11px] font-semibold tracking-wider uppercase"
          id={headingId}
        >
          {section.label}
          <span className="text-muted-foreground/70 text-[11px] font-normal tracking-normal normal-case">
            {section.tools.length}
          </span>
        </h3>
        {section.tools.map((tool) => renderToolRow(tool))}
      </section>
    )
  }

  const renderToolRow = (tool: HostedAgentToolCatalogEntry) => {
    const checked = form.tools.includes(tool.name)
    const isUnknown = !knownNames.has(tool.name)
    const isConfiguring = configuringTool === tool.name
    // library_search (and anything else in the bindings map) only returns
    // results once the matching tab has a binding — flag it before the admin
    // ships an agent whose search silently answers "nothing is enabled".
    const needsLibraryBinding =
      HOSTED_AGENT_TOOL_BINDINGS[tool.name] === "libraries" && form.libraries.length === 0
    return (
      <div key={tool.name} className="rounded-md border p-3 transition-colors">
        <div className="flex items-start gap-3">
          <label className="hover:bg-accent/50 flex min-w-0 flex-1 cursor-pointer items-start gap-3">
            <Checkbox
              checked={checked}
              className="mt-0.5"
              onCheckedChange={(value) => toggle(tool.name, value === true)}
            />
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="font-mono text-sm">{tool.name}</span>
                {tool.requiresApproval ? (
                  <Badge variant="warning">
                    {t("adminDashboard.hostedAgents.toolsTab.approvalBlocked", {
                      defaultValue: "Blocked for hosted agents",
                    })}
                  </Badge>
                ) : null}
                {!tool.requiresApproval && tool.approvalGatedActions.length > 0 ? (
                  <Badge variant="outline">
                    {t("adminDashboard.hostedAgents.toolsTab.approvalPartial", {
                      defaultValue: "Some actions blocked",
                    })}
                  </Badge>
                ) : null}
                {HOSTED_AGENT_TOOL_BINDINGS[tool.name] ? (
                  <Badge variant={needsLibraryBinding ? "warning" : "outline"}>
                    {t("adminDashboard.hostedAgents.toolsTab.needsBinding", {
                      defaultValue: "Needs a binding",
                    })}
                  </Badge>
                ) : null}
                {overrideToolNames.includes(tool.name) ? (
                  <Badge variant="outline">
                    {t("adminDashboard.hostedAgents.toolsTab.hasOverrides", {
                      defaultValue: "Overrides",
                    })}
                  </Badge>
                ) : null}
                {(form.toolActions[tool.name] ?? []).length ? (
                  <Badge variant="outline">
                    {t("adminDashboard.hostedAgents.toolsTab.actionsRestricted", {
                      defaultValue: "Actions restricted",
                    })}
                  </Badge>
                ) : null}
                {isUnknown ? (
                  <Badge variant="warning">
                    {t("adminDashboard.hostedAgents.toolsTab.unknownTool", {
                      defaultValue: "Not in the registry",
                    })}
                  </Badge>
                ) : null}
              </span>
              {tool.description ? (
                <span className="text-muted-foreground line-clamp-2 block text-xs">
                  {tool.description}
                </span>
              ) : null}
              {tool.requiresApproval ? (
                <span className="border-foreground bg-warning text-warning-foreground mt-1 block rounded-md border px-2 py-1 text-xs">
                  {t("adminDashboard.hostedAgents.toolsTab.approvalBlockedHint", {
                    defaultValue:
                      "This tool writes and needs the user to approve every call. A hosted agent has no approval channel, so the gateway refuses it fail-closed — selecting it here gives the agent nothing.",
                  })}
                </span>
              ) : null}
              {HOSTED_AGENT_TOOL_BINDINGS[tool.name] === "libraries" ? (
                <span
                  className={cn(
                    "mt-1 block text-xs",
                    needsLibraryBinding
                      ? "border-foreground bg-warning text-warning-foreground rounded-md border px-2 py-1"
                      : "text-muted-foreground"
                  )}
                >
                  {t("adminDashboard.hostedAgents.toolsTab.needsLibraryBindingHint", {
                    defaultValue:
                      "Only searches libraries bound to the agent — pick them in the Libraries tab.",
                  })}
                </span>
              ) : null}
            </span>
          </label>
          {checked ? (
            <Button
              aria-expanded={isConfiguring}
              className="shrink-0"
              size="icon"
              type="button"
              variant={isConfiguring ? "secondary" : "ghost"}
              aria-label={t("adminDashboard.hostedAgents.toolsTab.configureOverrides", {
                defaultValue: "Configure limits for {{name}}",
                name: tool.name,
              })}
              onClick={() => setConfiguringTool(isConfiguring ? null : tool.name)}
            >
              <SlidersHorizontal className="size-4" />
            </Button>
          ) : null}
        </div>
        {checked && isConfiguring ? (
          <>
            {renderOverrideEditor(tool.name)}
            <HostedAgentActionPicker form={form} toggleAction={toggleAction} tool={tool} />
          </>
        ) : null}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div>
        {form.tools.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("adminDashboard.hostedAgents.toolsTab.noneSelected", {
              defaultValue: "No tools selected — the agent will run without tools.",
            })}
          </p>
        ) : (
          <>
            <p className="text-muted-foreground mb-1.5 text-xs font-medium">
              {t("adminDashboard.hostedAgents.toolsTab.selectedLabel", {
                defaultValue: "Selected tools ({{count}})",
                count: form.tools.length,
              })}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {form.tools.map((tool) => (
                <Badge key={tool} className="gap-1 pr-1 font-mono" variant="outline">
                  {tool}
                  <button
                    className="hover:bg-accent -m-1 flex size-6 items-center justify-center rounded-sm"
                    type="button"
                    aria-label={t("adminDashboard.hostedAgents.toolsTab.removeTool", {
                      defaultValue: "Remove {{name}}",
                      name: tool,
                    })}
                    onClick={() => toggle(tool, false)}
                  >
                    <X className="size-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </>
        )}
      </div>

      {form.tools.includes("library_search") && form.libraries.length === 0 ? (
        <p className="border-foreground bg-warning text-warning-foreground rounded-md border px-2 py-1 text-xs">
          {t("adminDashboard.hostedAgents.toolsTab.librarySearchNeedsBinding", {
            defaultValue:
              "library_search needs at least one library bound to the agent — pick them in the Libraries tab.",
          })}
        </p>
      ) : null}
      {droppedOverrides.length > 0 ? (
        <Alert>
          <AlertDescription>
            {t("adminDashboard.hostedAgents.toolsTab.overridesWarning", {
              defaultValue: "Saving will also remove the tool overrides configured for: {{tools}}",
              tools: droppedOverrides.join(", "),
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          className="pl-9"
          value={search}
          aria-label={t("adminDashboard.hostedAgents.toolsTab.searchPlaceholder", {
            defaultValue: "Search tools…",
          })}
          placeholder={t("adminDashboard.hostedAgents.toolsTab.searchPlaceholder", {
            defaultValue: "Search tools…",
          })}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {isLoadingCatalog ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : null}
      {!isLoadingCatalog && catalogFailed ? (
        <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm">
          {t("adminDashboard.hostedAgents.toolsTab.loadError", {
            defaultValue: "Failed to load the tool registry.",
          })}
        </p>
      ) : null}
      {!isLoadingCatalog && !catalogFailed && filteredRows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t("adminDashboard.hostedAgents.toolsTab.catalogEmpty", {
            defaultValue: "No tools match the search.",
          })}
        </p>
      ) : null}
      {!isLoadingCatalog && !catalogFailed && (groupedRows.length > 0 || unknownRows.length > 0) ? (
        <div className="max-h-96 space-y-4 overflow-y-auto pr-1">
          {unknownRows.length > 0
            ? renderToolSection({
                id: "unknown",
                label: t("adminDashboard.hostedAgents.toolsTab.unknownTool", {
                  defaultValue: "Not in the registry",
                }),
                tools: unknownRows,
              })
            : null}
          {groupedRows.map(({ group, tools }) =>
            renderToolSection({ id: group, label: groupLabel(group), tools })
          )}
        </div>
      ) : null}
    </div>
  )
}
